import { useState } from "react";
import { toast } from "sonner";
import { api, categoryLabel, fmtMoney } from "@/lib/api";
import { FileDoc, UploadSimple, Check } from "@phosphor-icons/react";

export default function BulkImporter({ onDone }) {
  const [file, setFile] = useState(null);
  const [rows, setRows] = useState([]);
  const [processing, setProcessing] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [selected, setSelected] = useState(new Set());

  const pick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setRows([]);
  };

  const extract = async () => {
    if (!file) return;
    setProcessing(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/assets/parse-document", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 180000,
      });
      setRows(res.data.assets || []);
      setSelected(new Set((res.data.assets || []).map((_, i) => i)));
      if ((res.data.assets || []).length === 0) {
        toast.error("No structured items could be extracted from the document.");
      } else {
        toast.success(`${res.data.count} items parsed. Review below.`);
      }
    } catch (e) {
      toast.error("Parsing failed: " + (e?.response?.data?.detail || e.message));
    } finally {
      setProcessing(false);
    }
  };

  const toggle = (i) => {
    const s = new Set(selected);
    if (s.has(i)) s.delete(i); else s.add(i);
    setSelected(s);
  };

  const commit = async () => {
    setCommitting(true);
    let ok = 0;
    for (const [i, row] of rows.entries()) {
      if (!selected.has(i)) continue;
      try {
        await api.post("/assets", {
          name: row.name || "Untitled",
          category: row.category || "other",
          brand: row.brand || null,
          model: row.model || null,
          serial: row.serial || null,
          purchase_date: row.purchase_date || null,
          purchase_price: row.purchase_price ?? null,
          fmv: row.fmv ?? null,
          rcv: row.rcv ?? null,
          location: row.location || null,
          provenance: row.provenance || null,
          condition: row.condition || null,
          dispensation: row.dispensation || null,
          notes: row.notes || null,
        });
        ok++;
      } catch (_) {
        // Per-row failure — keep going; counted via `ok` below.
      }
    }
    setCommitting(false);
    toast.success(`${ok} of ${selected.size} items committed to the ledger.`);
    onDone?.();
  };

  return (
    <div data-testid="bulk-importer" className="space-y-6">
      <div>
        <div className="font-mono-ledger text-[10px] uppercase tracking-[0.25em] text-[#5C5F58] mb-2">
          Bulk Document Scraper
        </div>
        <h2 className="font-serif-display text-3xl text-[#0A192F]">Upload the old inventory.</h2>
        <p className="text-sm text-[#5C5F58] mt-3 max-w-2xl">
          A handwritten spreadsheet transcribed to Word. A PDF insurance schedule. A text file someone made years ago. Drop it in — we&apos;ll map every line into a formal ledger entry.
        </p>
      </div>

      <div className="bg-[#F9F8F6] border border-[#E0DDD6] p-6">
        <div className="flex flex-wrap items-center gap-4">
          <label className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#1A3622] text-[#F9F8F6] text-sm cursor-pointer hover:bg-[#0F2818] border border-[#0F2818]">
            <UploadSimple size={16} /> Choose document
            <input type="file" accept=".txt,.pdf,.docx,.md" onChange={pick} className="hidden" data-testid="doc-upload" />
          </label>
          {file && (
            <>
              <div className="flex items-center gap-2 text-sm text-[#0A192F]">
                <FileDoc size={16} color="#5C5F58" />
                {file.name}
              </div>
              <button
                onClick={extract}
                disabled={processing}
                data-testid="extract-doc"
                className="ml-auto px-5 py-2.5 text-sm bg-white border border-[#1A3622] text-[#1A3622] hover:bg-[#1A3622] hover:text-[#F9F8F6] disabled:opacity-60"
              >
                {processing ? "Parsing…" : "Extract Assets"}
              </button>
            </>
          )}
        </div>
        <p className="font-mono-ledger text-[10px] uppercase tracking-[0.25em] text-[#5C5F58] mt-4">
          Supported · TXT · MD · DOCX · PDF
        </p>
      </div>

      {rows.length > 0 && (
        <div className="space-y-4" data-testid="bulk-results">
          <div className="flex justify-between items-end border-b border-[#E0DDD6] pb-3">
            <div>
              <h3 className="font-serif-display text-2xl text-[#0A192F]">Review & commit</h3>
              <p className="text-xs text-[#5C5F58] mt-1">
                {selected.size} of {rows.length} selected for the ledger.
              </p>
            </div>
            <button
              onClick={commit}
              disabled={committing || selected.size === 0}
              data-testid="commit-bulk"
              className="px-5 py-2.5 text-sm bg-[#1A3622] text-[#F9F8F6] hover:bg-[#0F2818] disabled:opacity-60"
            >
              {committing ? "Committing…" : `Commit ${selected.size} Entries`}
            </button>
          </div>

          <div className="border border-[#E0DDD6] bg-white overflow-x-auto">
            <table className="w-full text-sm min-w-[720px]">
              <thead className="bg-[#F9F8F6] border-b border-[#E0DDD6]">
                <tr>
                  <th className="w-10" />
                  <th className="px-4 py-3 text-left font-mono-ledger text-[10px] uppercase tracking-[0.2em] text-[#5C5F58]">Name</th>
                  <th className="px-4 py-3 text-left font-mono-ledger text-[10px] uppercase tracking-[0.2em] text-[#5C5F58]">Class</th>
                  <th className="px-4 py-3 text-left font-mono-ledger text-[10px] uppercase tracking-[0.2em] text-[#5C5F58]">Brand/Model</th>
                  <th className="px-4 py-3 text-right font-mono-ledger text-[10px] uppercase tracking-[0.2em] text-[#5C5F58]">FMV</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i} className="border-b border-[#E0DDD6]" data-testid={`bulk-row-${i}`}>
                    <td className="pl-4 py-3">
                      <button
                        onClick={() => toggle(i)}
                        className={`w-5 h-5 border flex items-center justify-center ${selected.has(i) ? "bg-[#1A3622] border-[#1A3622]" : "border-[#C9C4B8]"}`}
                      >
                        {selected.has(i) && <Check size={12} color="#F9F8F6" weight="bold" />}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-[#0A192F]">{r.name || "—"}</td>
                    <td className="px-4 py-3 text-[#5C5F58]">{categoryLabel(r.category)}</td>
                    <td className="px-4 py-3 text-[#5C5F58]">{[r.brand, r.model].filter(Boolean).join(" · ") || "—"}</td>
                    <td className="px-4 py-3 text-right font-mono-ledger text-[#0A192F] tabular-nums">{fmtMoney(r.fmv)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
